import { useEffect, useState } from "react";
import { Flame, Shield } from "lucide-react";
import { cargarRachaDiaria, HITOS } from "../../lib/rachaDiaria";
import { fechaKey } from "../../lib/turnoRacha";
import { trackEvent } from "../../lib/eventTracking";
import RachaSheet from "./RachaSheet";

// Widget del gancho diario en el Home: la racha de "días seguidos" + el estado de
// hoy + un menú de "¿qué haces hoy?" (el usuario elige la actividad). El motor
// vive en lib/rachaDiaria; aquí solo se muestra y se ofrecen accesos. Tono
// anti-culpa: celebra la cadena, no reprocha el corte (los escudos la protegen).
// Auditoría editorial 2026-09-28 (E09): la racha es DESCRIPTIVA, nunca una
// obligación ("haz una cosa hoy para no romperla" salió); usar la app no
// demuestra aprendizaje ni calidad del cuidado.
// Tocar la tarjeta (fuera de los chips de actividad) abre la hoja "Tu racha"
// con el detalle completo (RachaSheet).
//
// Props: userId · actividades = [{ id, label, icon, onClick }] (adaptadas a la capa)
// · hoyLibre = hoy no te toca turno según tu rotación (el perdón por calendario:
//   la tarjeta no puede amenazar con romper la racha en tu día de descanso).
// · compacta = versión de una línea para el Home minimal (poda pre-Play
//   2026-08-02): mismo motor y misma hoja de detalle, la mitad del alto; las
//   actividades pasan a fila horizontal deslizable solo si falta el día.
export default function RachaDiariaWidget({ userId, actividades = [], hoyLibre = false, compacta = false }) {
  const [estado, setEstado] = useState(null); // { racha, max, fecha, escudos, dias }
  const [sheetAbierto, setSheetAbierto] = useState(false);

  useEffect(() => {
    if (!userId) return;
    let vivo = true;
    cargarRachaDiaria(userId).then((r) => { if (vivo) setEstado(r); });
    return () => { vivo = false; };
  }, [userId]);

  useEffect(() => {
    if (estado) trackEvent("racha_diaria_vista", { modulo: "home", payload: { racha: estado.racha, cumplido_hoy: estado.fecha === fechaKey() } });
  }, [estado]);

  if (!estado) return null;

  const racha       = estado.racha || 0;
  const cumplidoHoy = estado.fecha === fechaKey();
  const esHito      = cumplidoHoy && HITOS.includes(racha);

  const elegir = (e, a) => {
    e.stopPropagation(); // elegir actividad no debe abrir la hoja de detalle
    trackEvent("racha_diaria_actividad", { modulo: "home", payload: { actividad: a.id } });
    a.onClick?.();
  };

  const abrirSheet = () => {
    trackEvent("racha_sheet_abierta", { modulo: "home" });
    setSheetAbierto(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" || e.key === " ") { e.preventDefault(); abrirSheet(); }
  };

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        aria-label={`Ver el detalle de tu racha: ${racha === 0 ? "aún no empieza" : racha === 1 ? "1 día seguido" : `${racha} días seguidos`}`}
        onClick={abrirSheet}
        onKeyDown={handleKeyDown}
        className={`min-h-[44px] cursor-pointer rounded-tk-lg border border-edge bg-paper ${compacta ? "p-3" : "p-4"} shadow-tk-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/40`}
      >
        <div className="flex items-center gap-3">
          {/* La racha no es una señal clínica: su color es el acento, jamás el
              semáforo clinical-* (el color es urgencia, no identidad; A25). */}
          <span className={`grid place-items-center ${compacta ? "w-9 h-9" : "w-11 h-11"} rounded-full flex-shrink-0 ${racha > 0 ? "bg-accent-bg text-accent" : "bg-surface text-ink-muted"}`}>
            <Flame size={compacta ? 18 : 22} strokeWidth={1.9} />
          </span>
          <div className="flex-1 min-w-0">
            <p className={`${compacta ? "text-body" : "text-h3"} font-semibold text-ink font-sans leading-tight`}>
              {racha === 0 ? "Empieza tu racha" : racha === 1 ? "1 día seguido" : `${racha} días seguidos`}
            </p>
            <p className={`text-small font-sans leading-snug ${cumplidoHoy ? "text-system-success font-medium" : "text-ink-muted"}`}>
              {cumplidoHoy
                ? "✓ Hoy ya cuenta · nos vemos mañana"
                : racha === 0
                  ? "Cualquier actividad de hoy la empieza."
                  : hoyLibre
                    ? "Hoy estás libre según tu calendario. La racha se mantiene."
                    : "Cualquier actividad de hoy la suma."}
            </p>
          </div>
          {estado.escudos > 0 && (
            <span
              className="flex items-center gap-0.5 flex-shrink-0 text-ink-muted"
              title={`${estado.escudos} escudo${estado.escudos === 1 ? "" : "s"}: protege un día perdido`}
            >
              <Shield size={15} strokeWidth={1.8} className="text-accent" />
              <span className="text-small font-semibold font-sans">{estado.escudos}</span>
            </span>
          )}
        </div>

        {esHito && (
          <p className="mt-2 text-small font-semibold text-accent font-sans">
            ¡{racha} días seguidos! Vas increíble.
          </p>
        )}

        {/* Cuando el día ya está cumplido, la tarjeta queda compacta (solo la línea
            verde de arriba). Las actividades aparecen únicamente si falta el día. */}
        {!cumplidoHoy && actividades.length > 0 && (
          <div className={compacta ? "mt-2.5" : "mt-3"}>
            {!compacta && <p className="text-micro text-ink-faint font-sans mb-2 uppercase tracking-wide">¿Qué haces hoy?</p>}
            <div
              className={compacta ? "flex gap-2 overflow-x-auto pb-0.5 -mx-1 px-1" : "flex flex-wrap gap-2"}
              style={compacta ? { WebkitOverflowScrolling: "touch" } : undefined}
            >
              {actividades.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={(e) => elegir(e, a)}
                  className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-full border border-edge bg-paper px-3 py-1.5 text-small font-medium text-ink font-sans hover:border-accent-border transition-colors"
                >
                  {a.icon}
                  {a.label}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <RachaSheet
        abierto={sheetAbierto}
        onCerrar={() => setSheetAbierto(false)}
        racha={estado.racha}
        max={estado.max}
        dias={estado.dias}
        escudos={estado.escudos}
        userId={userId}
      />
    </>
  );
}
